/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_ORGANIZATION_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import { IOrganisationData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  CREATE_ORG_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";
import OrgContext from "../../store/orgContext";

const set = new Set();

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_ORGANIZATION_FORM_FIELDS.map(
    (field: any) => {
      return field.isInputInvalid?.(data[field.name]);
    }
  ).reduce((acc: any, el: any) => acc || el);
  return isConnectionFormInvalid;
};

const NewOrganizationForm = () => {
  const { token } = useContext(AuthContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<IOrganisationData>({
    "Organisation ID": "",
    "Organisation Name": "",
  });

  const createOrganization = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/organisations`,
        method: "POST",
        body: JSON.stringify({
          "ORG-ID": connectionData["Organisation ID"],
          "ORG-NAME": connectionData["Organisation Name"],
        }),
        token,
        emptyResponse: true,
      });
      successAlert(CREATE_ORG_TRANSLATIONS.sucessMsg);
      // update organisation list after creating a new organisation
      setOrgs([
        {
          id: connectionData["Organisation ID"],
          text: `${connectionData["Organisation Name"]} (${connectionData["Organisation ID"]})`,
        },
        ...orgs,
      ]);
      navigate("/configurationManagement/organizations");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            <div className="grid grid-cols-1 gap-4 form-control single-item">
              {ADD_ORGANIZATION_FORM_FIELDS.map(
                (field: {
                  name: keyof IOrganisationData;
                  tooltipText?: string;
                  label: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  required?: boolean;
                }) => {
                  return (
                    <TextField
                      onChange={(val) => {
                        set.add(field.name);
                        setConnectionData({
                          ...connectionData,
                          [field.name]: val,
                        });
                      }}
                      placeholder={field.label}
                      value={connectionData[field.name] as string}
                      invalid={
                        set.has(field.name) &&
                        field.isInputInvalid?.(
                          connectionData[field.name] as string
                        )
                      }
                      invalidText={
                        field.invalidText ||
                        FORM_VALIDATION_TRANSLATIONS.required(field.label)
                      }
                      labelText={field.label}
                      tooltipText={field.tooltipText}
                      required
                    />
                  );
                }
              )}
            </div>
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_ORG_TRANSLATIONS.cancelModal.header}
        modalDescriptionText={CREATE_ORG_TRANSLATIONS.cancelModal.modalContent}
        onDelete={() => navigate("/configurationManagement/organizations")}
        open={open}
        setOpen={setOpen}
        cancelIcon={DeleteModalIcons.Cancel}
        confirmationButtonLabel={CREATE_ORG_TRANSLATIONS.buttonLabels.goBack}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_ORG_TRANSLATIONS.buttonLabels.cancel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateOrganisation-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createOrganization}
            label={CREATE_ORG_TRANSLATIONS.buttonLabels.create}
            dataCy="createOrganisation-"
          />
        </div>
      </div>
    </>
  );
};

export default NewOrganizationForm;
